import React, { useState } from "react";
import { forgotPassword } from "../api/user";
import { Link } from "react-router-dom";
import ProcessingButton from "../components/Skeleton/ProcessingButton";
import PageContainer from "../components/pages/PageContainer";
import qushiLogo from "../images/logo-green.png";
import useAuthScreenBackground from "../hooks/useAuthScreenBackground";

const QushiLogo = () => (
  <img src={qushiLogo} alt="QUSHi" style={{ width: "120px" }} />
);

// Two sentences. On desktop each gets its own line: at that width the second
// sentence would otherwise start mid-line under the heading, which read badly.
// On mobile the column is far narrower, so an enforced break just leaves a
// short orphan line - there the copy wraps naturally instead. Same words and
// same size on both.
const HELPER_LINE_1 = "Enter the email associated with your QUSHi account.";
const HELPER_LINE_2 = "We'll send you a password reset link.";

// Deliberately the same sentence whether or not the address is registered.
// The old screen surfaced the API's "This email does not exist.", which turned
// this form into a way to test which addresses have QUSHi accounts.
const SUBMITTED_COPY =
  "If an account is associated with that email, we sent a password reset link.";

function Password() {
  // Cream all the way to the screen edges - see the hook for why the wrapper
  // div alone could not reach behind the status bar and home indicator.
  useAuthScreenBackground();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isMobileView] = useState(window.innerWidth < 768);

  const handleResetPassword = async (e) => {
    e.preventDefault();

    // The only thing worth flagging inline is a malformed address - anything
    // else would leak whether the account exists.
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError("Enter a valid email address.");
      setSubmitted(false);
      return;
    }

    setError(null);
    setLoading(true);
    try {
      await forgotPassword({ email: email });
    } catch (err) {
      // Swallowed on purpose: a rejected address must look identical to an
      // accepted one. Genuine failures are still visible in the console.
      console.error("Error:", err);
    }
    setLoading(false);
    setSubmitted(true);
  };

  // Mobile carries slightly heavier supporting text than desktop. At phone size
  // the desktop's small light-grey helper and label read as disabled next to the
  // large heading, so they step up to the weight the signup screens use for
  // their own body copy.
  const bodyClass = isMobileView
    ? "text-gray-700 mb-8 font-montserrat text-base leading-relaxed"
    : "text-gray-600 mb-8 font-montserrat text-sm leading-relaxed";
  const labelClass = isMobileView
    ? "block text-gray-600 text-sm font-semibold tracking-wide mb-2 font-montserrat"
    : "block text-gray-500 text-xs tracking-wide mb-2 font-montserrat";

  const form = (
    <form onSubmit={handleResetPassword} className="w-full">
      <h1 className="text-primaryColor font-bold text-4xl mb-4 font-montserrat">
        Reset your password
      </h1>
      <p className={bodyClass}>
        {isMobileView ? (
          `${HELPER_LINE_1} ${HELPER_LINE_2}`
        ) : (
          <>
            <span className="block">{HELPER_LINE_1}</span>
            <span className="block">{HELPER_LINE_2}</span>
          </>
        )}
      </p>

      <label htmlFor="reset-email" className={labelClass}>
        Email address
      </label>
      {/* type="text", not "email": the browser's own validation bubble would
          fire first on a malformed address and the client's wording
          ("Enter a valid email address.") would never be seen. The signup form
          does the same for the same reason. */}
      {/* size={1} is what keeps the desktop column the width of the heading.
          An <input> with no size attribute reports an intrinsic width of about
          20 characters - here 499px, wider than the 393px title - and a
          max-content column sizes to that, which is how the field and button
          ended up overhanging the title. The visible width still comes from
          w-full. */}
      <input
        id="reset-email"
        size={1}
        type="text"
        inputMode="email"
        autoComplete="email"
        name="email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (error) setError(null);
        }}
        placeholder="email@example.com"
        aria-invalid={error ? "true" : undefined}
        className="w-full rounded-full bg-gray-200 placeholder-gray-500 text-gray-700 px-5 py-4 focus:outline-none"
      />
      {error ? (
        <span role="alert" className="block text-red-500 text-xs mt-2 ml-2">
          {error}
        </span>
      ) : null}

      <ProcessingButton
        type="submit"
        processing={loading}
        label="Send Reset Link"
        processingLabel="Sending"
        className="w-full bg-primaryColor text-white rounded-2xl py-4 mt-6 font-bold text-lg focus:outline-none active:opacity-80 transition"
      />

      {/* width:0 + min-width:100% makes this fill the column while contributing
          nothing to its intrinsic width. Without it, this one long sentence is
          wider than the heading and would stretch the whole column the moment
          it appeared. */}
      {submitted ? (
        <p
          role="status"
          className="text-primaryColor text-sm mt-4 text-center font-montserrat"
          style={{ width: 0, minWidth: "100%" }}
        >
          {SUBMITTED_COPY}
        </p>
      ) : null}

      <div className="text-center mt-6">
        <Link
          to="/auth/login"
          className="text-primaryColor font-bold underline font-montserrat"
        >
          Back to Sign In
        </Link>
      </div>
    </form>
  );

  // Mobile: the same full-width treatment as the signup screens. The wordmark
  // sits BELOW "Back to Sign In" and stays in normal flow rather than pinned to
  // the bottom, so it cannot collide with the form when the keyboard opens or
  // on a short device.
  if (isMobileView) {
    return (
      <PageContainer>
        <div
          className="min-h-screen w-full flex flex-col justify-center px-8 py-12"
          style={{ backgroundColor: "#F2F0EC" }}
        >
          {form}
          <div className="flex justify-center py-6 mt-2">
            <QushiLogo />
          </div>
        </div>
      </PageContainer>
    );
  }

  // Desktop: the split screen the signup screens use.
  //
  // The column is sized to its own heading (width: max-content) rather than to a
  // fixed 448px. At 448px the field and the button ran 55px past the right edge
  // of "Reset your password", which is the overhang the client marked. Letting
  // the heading set the width keeps every control flush with the title on both
  // sides, and the heading still holds one line because the column is exactly as
  // wide as the unwrapped text.
  return (
    <PageContainer>
      <div className="flex min-h-screen">
        <div className="w-1/2 bg-primaryColor flex flex-col items-center justify-center">
          <img src="/Logo.png" alt="QUSHi" className="w-64" />
          <p
            className="text-white text-sm mt-4 uppercase whitespace-nowrap"
            style={{ letterSpacing: "0.3em", marginRight: "-0.3em" }}
          >
            TRADE TRACK PROVE IT
          </p>
        </div>
        <div
          className="w-1/2 flex items-center justify-center px-12 py-12 min-h-screen"
          style={{ backgroundColor: "#F2F0EC" }}
        >
          <div style={{ width: "max-content", maxWidth: "100%" }}>{form}</div>
        </div>
      </div>
    </PageContainer>
  );
}

export default Password;
